/**
 * Sorting for the Borrowing / Lending lists. The API orders the rows (`sort_by` + `sort_order`),
 * so this only holds the choice, its labels and its request params. One choice drives both views:
 * the grid's Sort dropdown and the list's column headers.
 */

export type LoanListSortField = 'name' | 'amount' | 'apr' | 'duration' | 'start_date' | 'due_date' | 'repaid_date' | 'appraisal' | 'ltv';
export type SortDirection = 1 | -1;

/** Any list's sort choice - the shared Sort dropdown and sortable headers take this, so other pages reuse them. */
export interface ListSort<F extends string = string> {
    field: F;
    direction: SortDirection;
}

export interface ListSortOption<F extends string = string> extends ListSort<F> {
    label: string;
}

export interface LoanListSort extends ListSort<LoanListSortField> { }

export interface LoanListSortOption extends ListSortOption<LoanListSortField> { }

/** The dropdown wording per field, ascending first - in the style of My Wallet's Sort menu. */
const SORT_LABELS: Record<LoanListSortField, [string, string]> = {
    name: ['Asset name (A → Z)', 'Asset name (Z → A)'],
    amount: ['By amount (0 → 9)', 'By amount (9 → 0)'],
    apr: ['By APR (0 → 9)', 'By APR (9 → 0)'],
    duration: ['By duration (0 → 9)', 'By duration (9 → 0)'],
    start_date: ['By start date (Oldest → Newest)', 'By start date (Newest → Oldest)'],
    due_date: ['By due date (Oldest → Newest)', 'By due date (Newest → Oldest)'],
    repaid_date: ['By repaid date (Oldest → Newest)', 'By repaid date (Newest → Oldest)'],
    appraisal: ['By appraisal (0 → 9)', 'By appraisal (9 → 0)'],
    ltv: ['By LTV (0 → 9)', 'By LTV (9 → 0)']
};

/** The dropdown options for the columns a page can sort, each field ascending then descending. */
export function buildLoanListSortOptions(fields: LoanListSortField[]): LoanListSortOption[] {
    return fields.flatMap((field) => [
        { field, direction: 1 as const, label: SORT_LABELS[field][0] },
        { field, direction: -1 as const, label: SORT_LABELS[field][1] }
    ]);
}

/** A column header click: ascending, then descending, then back to the default order. */
export function nextLoanListSort<F extends string>(current: ListSort<F> | null, field: F): ListSort<F> | null {
    if (current?.field !== field) return { field, direction: 1 };
    return current.direction === 1 ? { field, direction: -1 } : null;
}

export function loanListSortState<F extends string>(current: ListSort<F> | null, field: F): 'ascending' | 'descending' | 'none' {
    if (current?.field !== field) return 'none';
    return current.direction === 1 ? 'ascending' : 'descending';
}

export function isSameLoanListSort(a: ListSort | null, b: ListSort | null): boolean {
    return (a?.field ?? null) === (b?.field ?? null) && (a?.direction ?? null) === (b?.direction ?? null);
}

/** `sort` if its column is on the page (e.g. Repaid date only on the Repaid tab), otherwise none. */
export function keepLoanListSort<S extends ListSort>(sort: S | null, fields: string[]): S | null {
    return sort && fields.includes(sort.field) ? sort : null;
}

/** The request params, e.g. { sort_by: 'apr', sort_order: 'desc' } - none without a sort. */
export function loanListSortParams(sort: ListSort | null | undefined): Record<string, string> {
    if (!sort) return {};
    return { sort_by: sort.field, sort_order: sort.direction === 1 ? 'asc' : 'desc' };
}
